import { Bot } from 'lucide-react'
import { ClearFiltersIcon, SyncDesktopIcon } from '@/components/icons'
import { SortableTableHead } from '@/components/projects/SortableTableHead'
import { TestCaseStatusBadge } from '@/components/projects/TestCaseStatusBadge'
import { DeleteTestCaseDialog, EditTestCaseDialog, TestCaseDetailPanel, TestCaseSelectionBar } from '@/components/projects/testing'
import { PushTestItemToDevOpsDialog } from '@/components/projects/testing/PushTestItemToDevOpsDialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import { MultiSelect } from '@/components/ui/multi-select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useAuth,
  useConnectors,
  useDebounce,
  usePagination,
  useTestCase,
  useTestCasesByTestSuite,
  useTestSuiteDetails,
} from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { cn, getInitials } from '@/lib/utils'
import type { TestCase, TestCaseFilters } from '@/types'
import { TEST_CASE_DEFAULT_SORT_DIRECTIONS, testCaseStatusFilterOptions, type TestCaseSortColumn } from '@/types/testCase'
import { ArrowLeft, BoxSelect, Pencil, Search, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

const SORT_KEYS = ['sort', 'sortBy'] as const

export function TestCasesOverviewPage() {
  const { projectId, testSuiteId } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { hasPermission } = useAuth(projectId)

  const [search, setSearch] = useState('')
  const [statuses, setStatuses] = useState<string[]>([])
  const [deletingTestCase, setDeletingTestCase] = useState<TestCase | null>(null)
  const [editingTestCase, setEditingTestCase] = useState<TestCase | null>(null)
  const [pushingTestCase, setPushingTestCase] = useState<TestCase | null>(null)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkPushOpen, setBulkPushOpen] = useState(false)

  const selectedTestCaseId = searchParams.get('testCaseId')

  const setSelectedTestCaseId = (testCaseId: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (testCaseId) next.set('testCaseId', testCaseId)
    else next.delete('testCaseId')
    setSearchParams(next, { replace: false })
  }

  const { page, pageSize, paginationParams, setPage, setPageSize, resetPage } = usePagination()
  const debouncedSearch = useDebounce(search, 300)

  const sortStorageKey = `test-cases-sort-${testSuiteId}`

  const hasUrlSort = SORT_KEYS.some(k => searchParams.has(k))
  const sessionSort = useMemo(
    () => new URLSearchParams(hasUrlSort ? '' : (sessionStorage.getItem(sortStorageKey) ?? '')),
    [hasUrlSort, sortStorageKey],
  )

  const sortByParam = searchParams.get('sortBy') ?? sessionSort.get('sortBy')
  const sortBy: TestCaseSortColumn =
    sortByParam && sortByParam in TEST_CASE_DEFAULT_SORT_DIRECTIONS ? (sortByParam as TestCaseSortColumn) : 'number'
  const sortDirParam = searchParams.get('sort') ?? sessionSort.get('sort')
  const sortDirection: 'asc' | 'desc' =
    sortDirParam === 'asc' || sortDirParam === 'desc' ? sortDirParam : TEST_CASE_DEFAULT_SORT_DIRECTIONS[sortBy]

  const persistSort = useCallback((params: URLSearchParams) => {
    const sortParams = new URLSearchParams()
    SORT_KEYS.forEach(k => {
      const v = params.get(k)
      if (v) sortParams.set(k, v)
    })
    sessionStorage.setItem(sortStorageKey, sortParams.toString())
  }, [sortStorageKey])

  const didRestoreSortRef = useRef(false)
  useEffect(() => {
    if (didRestoreSortRef.current) return
    didRestoreSortRef.current = true
    if (SORT_KEYS.some(k => searchParams.has(k))) return
    const saved = sessionStorage.getItem(sortStorageKey)
    if (!saved) return
    const savedParams = new URLSearchParams(saved)
    if (!Array.from(savedParams.keys()).length) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      savedParams.forEach((value, key) => next.set(key, value))
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams, sortStorageKey])

  const handleSortChange = (column: TestCaseSortColumn) => {
    const nextDirection =
      column === sortBy ? (sortDirection === 'asc' ? 'desc' : 'asc') : TEST_CASE_DEFAULT_SORT_DIRECTIONS[column]
    const next = new URLSearchParams(searchParams)
    next.set('sortBy', column)
    next.set('sort', nextDirection)
    setSearchParams(next, { replace: true })
    persistSort(next)
    resetPage()
    clearSelection()
  }

  const filters = useMemo<TestCaseFilters>(() => {
    const f: TestCaseFilters = { sortBy, sortDirection }
    if (debouncedSearch) f.search = debouncedSearch
    if (statuses.length > 0) f.statuses = statuses
    return f
  }, [debouncedSearch, statuses, sortBy, sortDirection])

  useEffect(() => {
    resetPage()
  }, [debouncedSearch, statuses, resetPage])

  const { data: suiteDetails, isLoading: suiteLoading } = useTestSuiteDetails(
    projectId!,
    testSuiteId!,
  )
  const { data, isLoading } = useTestCasesByTestSuite(
    projectId!,
    testSuiteId!,
    paginationParams,
    filters,
  )

  const testCases = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  const canDelete = hasPermission(Permissions.Project.TestCase.Delete, projectId)
  const canEdit = hasPermission(Permissions.Project.TestCase.Update, projectId)
  const canPush = hasPermission(Permissions.Project.TestCase.Push, projectId)

  const { data: connectors = [] } = useConnectors(projectId!)
  const azureDevOpsConnected = useMemo(() => connectors.some(c => c.type === 'AzureDevOps'), [connectors])

  const selectedTestCases = testCases.filter(tc => selectedIds.has(tc.id))

  const allVisibleIds = testCases.map(tc => tc.id)
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selectedIds.has(id))
  const someSelected = allVisibleIds.some(id => selectedIds.has(id))

  const clearSelection = () => setSelectedIds(new Set())

  const handleToggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) setSelectedIds(new Set())
      return !prev
    })
  }

  const handleCancelSelection = () => {
    setSelectionMode(false)
    setSelectedIds(new Set())
  }

  const handleToggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleToggleAll = (ids: string[]) => {
    setSelectedIds((prev) => {
      const allIdsSelected = ids.length > 0 && ids.every(id => prev.has(id))
      if (allIdsSelected) {
        const next = new Set(prev)
        ids.forEach(id => next.delete(id))
        return next
      }
      return new Set([...prev, ...ids])
    })
  }

  const { data: editingTestCaseDetail } = useTestCase(projectId!, editingTestCase?.id ?? null)

  const handleBack = () => {
    navigate(`/projects/${projectId}/testing-integration/test-plans`)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="iconSm"
          onClick={handleBack}
          aria-label="Back to test plans"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        {suiteLoading ? (
          <Skeleton className="h-8 w-[420px]" />
        ) : suiteDetails ? (
          <h1 className="text-3xl font-bold">
            {suiteDetails.testPlanTitle}: {suiteDetails.title}
          </h1>
        ) : (
          <h1 className="text-3xl font-bold text-muted-foreground">Test suite not found</h1>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="The list is searchable by #, test case name, user"
            value={search}
            onChange={(e) => { setSearch(e.target.value); clearSelection() }}
            className="pl-9 pr-9"
            aria-label="Search test cases"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); clearSelection() }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <MultiSelect
          options={testCaseStatusFilterOptions}
          value={statuses}
          onChange={(v) => { setStatuses(v); clearSelection() }}
          placeholder="All Statuses"
          displayMode="compact"
          searchable={false}
          showCompactIcon={false}
          showCompactOverflowCount
          className="w-[200px]"
          aria-label="Filter by status"
        />
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setSearch('')
            setStatuses([])
            clearSelection()
            setSearchParams(prev => {
              const next = new URLSearchParams(prev)
              SORT_KEYS.forEach(k => next.delete(k))
              persistSort(next)
              return next
            }, { replace: true })
            resetPage()
          }}
          disabled={!search && statuses.length === 0 && sortBy === 'number' && sortDirection === TEST_CASE_DEFAULT_SORT_DIRECTIONS.number}
          className="flex items-center gap-1.5 text-sm font-medium"
          aria-label="Clear all filters"
        >
          <ClearFiltersIcon className="h-4 w-4" />
          Clear All
        </Button>
        <Button
          type="button"
          className={cn(
            'border border-border-accent',
            `${selectionMode ? 'bg-primary text-primary-foreground' : 'bg-toolbar'}`
          )}
          variant={selectionMode ? 'default' : 'outline'}
          size="icon"
          onClick={handleToggleSelectionMode}
          aria-label="Toggle multi-select mode"
          aria-pressed={selectionMode}
          data-testid="test-case-selection-mode-toggle"
        >
          <BoxSelect className="h-4 w-4" />
        </Button>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table aria-label="Test cases table">
          <TableHeader>
            <TableRow className="bg-muted/50">
              {selectionMode && (
                <TableHead className="w-[48px]">
                  <Checkbox
                    aria-label="Select all visible test cases"
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={() => handleToggleAll(allVisibleIds)}
                    data-testid="test-case-select-all"
                  />
                </TableHead>
              )}
              <SortableTableHead
                column="number"
                label="#"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={TEST_CASE_DEFAULT_SORT_DIRECTIONS}
                onSortChange={handleSortChange}
                className="w-[110px]"
              />
              <SortableTableHead
                column="title"
                label="Title"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={TEST_CASE_DEFAULT_SORT_DIRECTIONS}
                onSortChange={handleSortChange}
              />
              <SortableTableHead
                column="status"
                label="Status"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={TEST_CASE_DEFAULT_SORT_DIRECTIONS}
                onSortChange={handleSortChange}
                className="w-[130px]"
              />
              <SortableTableHead
                column="user"
                label="User"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={TEST_CASE_DEFAULT_SORT_DIRECTIONS}
                onSortChange={handleSortChange}
                className="w-[220px]"
              />
              {!selectionMode && <TableHead className="w-[170px]">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow role="status" aria-live="polite" aria-label="Loading test cases">
                <TableCell colSpan={5} className="p-0">
                  <div className="space-y-3 p-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ) : totalCount === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-muted-foreground" data-testid="test-cases-empty">
                  No test cases match your filters.
                </TableCell>
              </TableRow>
            ) : (
              testCases.map((tc) => (
                <TestCaseRow
                  key={tc.id}
                  testCase={tc}
                  canDelete={canDelete}
                  canEdit={canEdit}
                  canPush={canPush}
                  azureDevOpsConnected={azureDevOpsConnected}
                  selectionMode={selectionMode}
                  isSelected={selectedIds.has(tc.id)}
                  onToggleSelect={() => handleToggleRow(tc.id)}
                  onSelect={() => setSelectedTestCaseId(tc.id)}
                  onDelete={() => setDeletingTestCase(tc)}
                  onEdit={() => setEditingTestCase(tc)}
                  onPush={() => setPushingTestCase(tc)}
                />
              ))
            )}
          </TableBody>
        </Table>
        <Pagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={(p) => { setPage(p); clearSelection() }}
          onPageSizeChange={(s) => { setPageSize(s); clearSelection() }}
        />
      </div>

      {deletingTestCase && (
        <DeleteTestCaseDialog
          projectId={projectId!}
          testCase={deletingTestCase}
          open
          onOpenChange={(open) => { if (!open) setDeletingTestCase(null) }}
        />
      )}

      {editingTestCase && editingTestCaseDetail && (
        <EditTestCaseDialog
          projectId={projectId!}
          testCase={editingTestCaseDetail}
          open
          onOpenChange={(open) => { if (!open) setEditingTestCase(null) }}
        />
      )}

      {pushingTestCase && (
        <PushTestItemToDevOpsDialog
          projectId={projectId!}
          mode="case"
          itemId={pushingTestCase.id}
          itemTitle={pushingTestCase.title}
          itemIdentifier={pushingTestCase.testCaseIdentifier}
          statusBadge={<TestCaseStatusBadge status={pushingTestCase.status} />}
          open
          onOpenChange={(open) => { if (!open) setPushingTestCase(null) }}
        />
      )}

      {bulkPushOpen && (
        <PushTestItemToDevOpsDialog
          projectId={projectId!}
          mode="cases"
          items={selectedTestCases.map(tc => ({
            id: tc.id,
            identifier: tc.testCaseIdentifier,
            title: tc.title,
            statusBadge: <TestCaseStatusBadge status={tc.status} />,
          }))}
          open
          onOpenChange={setBulkPushOpen}
          onPushed={(result) => {
            if (result.pushed.length > 0 || result.skipped.length > 0) handleCancelSelection()
          }}
        />
      )}

      <TestCaseSelectionBar
        selectedCount={selectedIds.size}
        onCancel={handleCancelSelection}
        onPush={canPush ? () => setBulkPushOpen(true) : undefined}
        pushDisabled={!azureDevOpsConnected}
        pushDisabledReason="Connect Azure DevOps to enable push"
      />

      <TestCaseDetailPanel
        projectId={projectId!}
        testCaseId={selectedTestCaseId}
        open={selectedTestCaseId !== null}
        onOpenChange={(open) => { if (!open) setSelectedTestCaseId(null) }}
      />
    </div>
  )
}

interface TestCaseRowProps {
  testCase: TestCase
  canDelete: boolean
  canEdit: boolean
  canPush: boolean
  azureDevOpsConnected: boolean
  selectionMode: boolean
  isSelected: boolean
  onToggleSelect: () => void
  onSelect: () => void
  onDelete: () => void
  onEdit: () => void
  onPush: () => void
}

function TestCaseRow({ testCase, canDelete, canEdit, canPush, azureDevOpsConnected, selectionMode, isSelected, onToggleSelect, onSelect, onDelete, onEdit, onPush }: TestCaseRowProps) {
  return (
    <TableRow
      className="group cursor-pointer hover:bg-muted/50 transition-colors"
      data-state={selectionMode && isSelected ? 'selected' : undefined}
      onClick={selectionMode ? onToggleSelect : onSelect}
      data-testid={`test-case-row-${testCase.testCaseIdentifier}`}
    >
      {selectionMode && (
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Checkbox
            aria-label={`Select test case ${testCase.testCaseIdentifier}`}
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            data-testid={`test-case-select-${testCase.testCaseIdentifier}`}
          />
        </TableCell>
      )}
      <TableCell className="font-medium">{testCase.testCaseIdentifier}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2 min-w-0">
          <span className="min-w-0">{testCase.title}</span>
          {testCase.isAutomated && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-purple-foreground bg-purple px-1.5 py-0.5 text-[10px] font-medium text-purple-foreground shrink-0"
              title={`Automated · ${testCase.automationStatus}`}
              data-testid={`test-case-automated-badge-${testCase.testCaseIdentifier}`}
            >
              <Bot className="h-3 w-3" aria-hidden="true" />
              Automated
            </span>
          )}
        </div>
      </TableCell>
      <TableCell><TestCaseStatusBadge status={testCase.status} /></TableCell>
      <TableCell>
        {testCase.createdByUserName ? (
          <div className="flex items-center gap-2">
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                {getInitials(testCase.createdByUserName)}
              </AvatarFallback>
            </Avatar>
            <span className="min-w-0 text-sm">{testCase.createdByUserName}</span>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">&mdash;</span>
        )}
      </TableCell>
      {!selectionMode && (
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          {canPush && (
            <span
              className={azureDevOpsConnected ? 'inline-flex' : 'inline-flex cursor-not-allowed'}
              title={azureDevOpsConnected ? undefined : 'Connect Azure DevOps to enable push'}
            >
              <button
                type="button"
                className="flex items-center gap-1.5 px-2 py-1 text-sm font-medium hover:bg-primary/5 rounded-md disabled:opacity-50 disabled:pointer-events-none"
                onClick={onPush}
                disabled={!azureDevOpsConnected}
                aria-label={`Push test case ${testCase.testCaseIdentifier} to Azure DevOps`}
                data-testid={`push-test-case-button-${testCase.testCaseIdentifier}`}
              >
                <SyncDesktopIcon className="h-4 w-4 shrink-0 text-action-icon" />
                Push
              </button>
            </span>
          )}
          {canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={onEdit}
              aria-label={`Edit test case ${testCase.testCaseIdentifier}`}
              data-testid={`edit-test-case-button-${testCase.testCaseIdentifier}`}
            >
              <Pencil className="h-4 w-4 text-action-icon" />
            </Button>
          )}
          {canDelete && (
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={onDelete}
              aria-label={`Delete test case ${testCase.testCaseIdentifier}`}
              data-testid={`delete-test-case-button-${testCase.testCaseIdentifier}`}
            >
              <Trash2 className="h-4 w-4 text-action-icon" />
            </Button>
          )}
        </div>
      </TableCell>
      )}
    </TableRow>
  )
}
